import { useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { CustomButton } from "../../../components/common/customButton";
import { renderSubTitle } from "../../../components/renderComponents";
import {
  deleteDealImage,
  updateDealImage,
} from "../../../services/dealImagesApi";
import type { DealImage } from "../../../types/dealImages";
import type { ApiResponse } from "../../../services/genesisApi/httpsMethods";
import type { useDealImages } from "../../../hooks/useDealImages";
import DealImageUploadDialog from "./DealImageUploadDialog";
import { useErrorDialog } from "../../../hooks/useErrorDialog";
import { useCurrentUser } from "../../../context/CurrentUserContext";
import { ErrorDialog } from "../../../components/common/errorDialog";
import DealImageTypeChip from "./DealImageTypeChip";

type DealImagesState = ReturnType<typeof useDealImages>;

const galleryGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(1, minmax(0, 1fr))",
    sm: "repeat(2, minmax(0, 1fr))",
    lg: "repeat(4, minmax(0, 1fr))",
  },
  gap: 2,
  mt: 1.5,
} as const;

interface DealGalleryTabProps {
  dealId: number;
  imagesState: DealImagesState;
}

function ImageActions({
  image,
  actionUpdating,
  onDelete,
  onSetPrimary,
  canEdit,
}: {
  image: DealImage;
  actionUpdating: boolean;
  onDelete: (image: DealImage) => void;
  onSetPrimary?: (image: DealImage) => void;
  canEdit: boolean;
}) {
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
          {image.is_hero && (
            <Chip label="Primary" size="small" color="primary" />
          )}
          {image.source === "ai_extraction" && (
            <Chip
              label="AI"
              size="small"
              sx={{ backgroundColor: "#efe7ff", color: "#5b36a3" }}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          {onSetPrimary && !image.is_hero && (
            <CustomButton
              text="Set as primary"
              handleButtonPress={() => onSetPrimary(image)}
              solid={false}
              isButtonDisabled={actionUpdating || !canEdit}
              heightOverride="32px"
              widthOverride="120px"
            />
          )}
          <Tooltip title="Delete image">
            <span>
              <IconButton
                size="small"
                aria-label="Delete image"
                disabled={actionUpdating || !canEdit}
                onClick={() => onDelete(image)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </>
  );
}

function DealGalleryCard({
  image,
  actionUpdating,
  onDelete,
  onSetPrimary,
  onImageError,
  canEdit,
}: {
  image: DealImage;
  actionUpdating: boolean;
  onDelete: (image: DealImage) => void;
  onSetPrimary: (image: DealImage) => void;
  onImageError: () => void;
  canEdit: boolean;
}) {
  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        overflow: "hidden",
        backgroundColor: "background.paper",
      }}
    >
      <Box
        component="a"
        href={image.url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: "block",
          position: "relative",
          aspectRatio: "4/3",
          backgroundColor: "#f5f5f5",
        }}
      >
        <Box
          component="img"
          src={image.url}
          alt={image.caption ?? "Deal image"}
          onError={onImageError}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        {image.is_hero && (
          <Chip
            label="Primary"
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              backgroundColor: "#0E4664",
              color: "#fff",
              fontWeight: 600,
            }}
          />
        )}
        <DealImageTypeChip
          imageType={image.image_type}
          sx={{ position: "absolute", bottom: 8, left: 8 }}
        />
      </Box>
      <Box sx={{ p: 1.5 }}>
        <ImageActions
          image={image}
          actionUpdating={actionUpdating}
          onDelete={onDelete}
          onSetPrimary={onSetPrimary}
          canEdit={canEdit}
        />
        {image.caption && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {image.caption}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function DealGalleryTab({
  dealId,
  imagesState,
}: DealGalleryTabProps) {
  const { canEdit } = useCurrentUser();
  const { hero, images, loading, error, refetch } = imagesState;
  const [uploadOpen, setUploadOpen] = useState(false);
  const [actionUpdating, setActionUpdating] = useState(false);
  const { errorDialog, showError, hideError } = useErrorDialog();

  const galleryImages = hero
    ? images.filter((img) => img.id !== hero.id)
    : images;

  const runImageAction = async (
    action: () => Promise<ApiResponse<unknown>>,
    errorMessage: string,
    errorTitle: string,
  ) => {
    setActionUpdating(true);
    try {
      const response = await action();
      if (response?.success) {
        await refetch();
      } else {
        showError(
          response,
          typeof response?.message === "string" ? response.message : errorMessage,
          errorTitle,
        );
      }
    } finally {
      setActionUpdating(false);
    }
  };

  const handleDelete = async (image: DealImage) => {
    if (!window.confirm("Delete this image from the deal gallery?")) {
      return;
    }
    await runImageAction(
      () => deleteDealImage(dealId, image.id),
      "Cannot delete this image.",
      "Delete failed",
    );
  };

  const handleSetPrimary = async (image: DealImage) => {
    await runImageAction(
      () => updateDealImage(dealId, image.id, { is_hero: true }),
      "Cannot set primary image.",
      "Update failed",
    );
  };

  if (loading && !hero && images.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <CustomButton
          text="Upload Images"
          handleButtonPress={() => setUploadOpen(true)}
          solid={true}
          isButtonDisabled={actionUpdating || !canEdit}
          heightOverride="32px"
          widthOverride="140px"
        />
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {!hero && images.length === 0 ? (
        <Typography color="text.secondary">
          No images yet — upload or run an extraction with images enabled.
        </Typography>
      ) : (
        <>
          {hero && (
            <Box sx={{ mb: 3 }}>
              {renderSubTitle("Primary image")}
              <Box
                sx={{
                  mt: 1.5,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  overflow: "hidden",
                  maxWidth: 640,
                }}
              >
                <Box
                  component="a"
                  href={hero.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "block",
                    position: "relative",
                  }}
                >
                  <Box
                    component="img"
                    src={hero.url}
                    alt={hero.caption ?? "Hero image"}
                    onError={() => void refetch()}
                    sx={{
                      width: "100%",
                      display: "block",
                      maxHeight: 400,
                      objectFit: "cover",
                    }}
                  />
                  <DealImageTypeChip
                    imageType={hero.image_type}
                    sx={{ position: "absolute", bottom: 12, left: 12 }}
                  />
                </Box>
                <Box sx={{ p: 1.5 }}>
                  <ImageActions
                    image={hero}
                    actionUpdating={actionUpdating}
                    onDelete={handleDelete}
                    canEdit={canEdit}
                  />
                  {hero.caption && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {hero.caption}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {galleryImages.length > 0 && (
            <Box>
              {renderSubTitle(`Gallery (${galleryImages.length})`)}
              <Box sx={galleryGridSx}>
                {galleryImages.map((image) => (
                  <DealGalleryCard
                    key={image.id}
                    image={image}
                    actionUpdating={actionUpdating}
                    onDelete={handleDelete}
                    onSetPrimary={handleSetPrimary}
                    onImageError={() => void refetch()}
                    canEdit={canEdit}
                  />
                ))}
              </Box>
            </Box>
          )}
        </>
      )}

      <DealImageUploadDialog
        open={uploadOpen}
        dealId={dealId}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => void refetch()}
      />

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
}
